import reducerRegistry from '@comeon/reducer-registry';
import { requestApi, parseResponse } from '~/services/api.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import { currentGameSelector, gameModeSelector } from '~/components/casino-shared/game-loader/ducks/selectors';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectEnableInhouseJackpot } from '~/components/brand/ducks/selectors';
import currencyFormatter from '~/libs/currency-formatter.js';
import { createSelector } from 'reselect';

const REDUCER_NAME = 'inhouseJackpot';
const baseUrl = '/rest/casino/inhouse-jackpot/';

const ACTION_PREFIX = '@komigen/mobile-frontend/inhouse-jackpot';
export const FETCH_OPTEDIN_JACKPOTS_LOADING = `${ACTION_PREFIX}/FETCH_OPTEDIN_JACKPOTS_LOADING`;
export const OPTIN_LOADING = `${ACTION_PREFIX}/OPTIN_LOADING`;
export const OPTOUT_LOADING = `${ACTION_PREFIX}/OPTOUT_LOADING`;
export const SET_OPTEDIN_JACKPOTS = `${ACTION_PREFIX}/SET_OPTEDIN_JACKPOTS`;
export const SET_IS_MODAL_SHOWN = `${ACTION_PREFIX}/SET_IS_MODAL_SHOWN`;
export const SET_JACKPOT_DETAILS = `${ACTION_PREFIX}/SET_JACKPOT_DETAILS`;
export const SET_JACKPOT_TIERS = `${ACTION_PREFIX}/SET_JACKPOT_TIERS`;
export const UPDATE_JACKPOT_TIERS = `${ACTION_PREFIX}/UPDATE_JACKPOT_TIERS`;
export const JACKPOT_DETAILS_LOADING = `${ACTION_PREFIX}/JACKPOT_DETAILS_LOADING`;
export const SET_IS_WIN_ANIMATION_SHOWN = `${ACTION_PREFIX}/SET_IS_WIN_ANIMATION_SHOWN`;
export const SET_WIN_ANIMATION_DATA = `${ACTION_PREFIX}/SET_WIN_ANIMATION_DATA`;
export const INVALIDATE_INHOUSE_JACKPOT_STATE = `${ACTION_PREFIX}/INVALIDATE_INHOUSE_JACKPOT_STATE`;
export const JACKPOT_GAME_CLOSED = `${ACTION_PREFIX}/JACKPOT_GAME_CLOSED`;
export const JACKPOT_GAME_STARTED = `${ACTION_PREFIX}/JACKPOT_GAME_STARTED`;
export const SET_MAIN_INHOUSE_JP_ID = `${ACTION_PREFIX}/SET_MAIN_INHOUSE_JP_ID`;
export const SET_PREMIUM_INHOUSE_JP_ID = `${ACTION_PREFIX}/SET_PREMIUM_INHOUSE_JP_ID`;

const initialState = {
	optedinInhouseJackpots: [],
	fetchOptedinJackpotsLoading: false,
	jackpotDetailsLoading: false,
	optinLoading: false,
	optoutLoading: false,
	isModalShown: false,
	jackpotDetails: {},
	jackpotTiers: [],
	isWinAnimationShown: null,
	winAnimationData: null,
	isInGame: false,
	mainInhouseJackpotId: null,
	premiumInhouseJackpotId: null
};

const showGeneralError = dispatch => {
	dispatch(
		showNotification({
			color: 'red',
			autoHide: true,
			messageKey: 'INHOUSE_JACKPOT_GENERAL_REQUEST_ERROR'
		})
	);
};

// methods START
const sendRequest = async (dispatch, url, responseActionType, loadingActionType, method = 'GET', payload = {}) => {
	dispatch({
		type: loadingActionType,
		payload: true
	});
	try {
		let response = await requestApi(baseUrl + url, payload, method);
		if (response.status === 'FAILURE') {
			dispatch(
				showNotification({
					color: 'red',
					autoHide: true,
					message: response.result
				})
			);
			return;
		}
		response = parseResponse(response);
		await dispatch({
			type: responseActionType,
			payload: response
		});
		return response;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error);
		dispatch(
			showNotification({
				color: 'red',
				autoHide: true,
				messageKey: 'INHOUSE_JACKPOT_GENERAL_REQUEST_ERROR'
			})
		);
	} finally {
		dispatch({
			type: loadingActionType,
			payload: false
		});
	}
};

const addFormattedTierAmounts = (tiers, currency) => {
	return tiers.map(tier => {
		return {
			...tier,
			formattedTierValue: currencyFormatter.format(tier.value, currency, '', true)
		};
	});
};
// methods END

// selectors START
export const optedinInhouseJackpotsSelector = state => state.inhouseJackpot?.optedinInhouseJackpots;
export const fetchOptedinJackpotsLoadingSelector = state => state.inhouseJackpot?.fetchOptedinJackpotsLoading;
export const jackpotDetailsLoadingSelector = state => state.inhouseJackpot?.jackpotDetailsLoading;
export const optinLoadingSelector = state => state.inhouseJackpot?.optinLoading;
export const optoutLoadingSelector = state => state.inhouseJackpot?.optoutLoading;
export const isModalShownSelector = state => state.inhouseJackpot?.isModalShown;
export const jackpotDetailsSelector = state => state.inhouseJackpot?.jackpotDetails;
export const jackpotTiersSelector = state => state.inhouseJackpot?.jackpotTiers;
export const isWinAnimationShownSelector = state => state.inhouseJackpot?.isWinAnimationShown;
export const winAnimationDataSelector = state => state.inhouseJackpot?.winAnimationData;
export const isWinModalEnabledSelector = state => !state.inhouseJackpot?.isInGame;
export const mainInhouseJackpotSelector = state => state.inhouseJackpot?.mainInhouseJackpotId;
export const premiumInhouseJackpotSelector = state => state.inhouseJackpot?.premiumInhouseJackpotId;
export const inhouseJackpotAvailableInCurrentGameSelector = createSelector(
	currentGameSelector,
	gameModeSelector,
	authenticationSelector,
	selectEnableInhouseJackpot,
	(currentGame, currentGameMode, authenticated, isInhouseJackpotEnabled) => {
		return (
			authenticated &&
			isInhouseJackpotEnabled &&
			!!currentGame?.gameData?.inhouseJackpots?.length &&
			currentGameMode === 'real'
		);
	}
);
// selectors END

// action creators START
export const setIsModalShown = payload => {
	return {
		type: SET_IS_MODAL_SHOWN,
		payload
	};
};
export const setIsWinAnimationShown = payload => {
	return {
		type: SET_IS_WIN_ANIMATION_SHOWN,
		payload
	};
};
export const setWinAnimationData = payload => {
	return {
		type: SET_WIN_ANIMATION_DATA,
		payload
	};
};
export const invalidateInhouseJackpotState = () => {
	return { type: INVALIDATE_INHOUSE_JACKPOT_STATE };
};
export const setFetchOptedinJackpotsLoading = payload => ({
	type: FETCH_OPTEDIN_JACKPOTS_LOADING,
	payload
});
export const enableJackpotPopup = () => {
	return {
		type: JACKPOT_GAME_CLOSED
	};
};
export const disableJackpotPopup = () => {
	return {
		type: JACKPOT_GAME_STARTED
	};
};
export const updateJackpotTiers = payload => {
	return {
		type: UPDATE_JACKPOT_TIERS,
		payload
	};
};
export const setJackpotId = payload => {
	return {
		type: payload.isPremium ? SET_PREMIUM_INHOUSE_JP_ID : SET_MAIN_INHOUSE_JP_ID,
		payload
	};
};
// action creators END

// thunks START
export const fetchOptedInJackpots = () => {
	return async dispatch => {
		await sendRequest(dispatch, 'optin-jackpots', SET_OPTEDIN_JACKPOTS, FETCH_OPTEDIN_JACKPOTS_LOADING);
	};
};

export const optIn = jackpotId => {
	return async dispatch => {
		if (!jackpotId) {
			// eslint-disable-next-line no-console
			console.error('No jackpot id provided');
			showGeneralError(dispatch);
			return;
		}
		await sendRequest(dispatch, `optin/${jackpotId}`, SET_OPTEDIN_JACKPOTS, OPTIN_LOADING, 'PUT');
		await dispatch(fetchOptedInJackpots());
	};
};

export const optOut = jackpotId => {
	return async dispatch => {
		if (!jackpotId) {
			// eslint-disable-next-line no-console
			console.error('No jackpot id provided for opt out');
			showGeneralError(dispatch);
			return;
		}
		await sendRequest(dispatch, `optout/${jackpotId}`, SET_OPTEDIN_JACKPOTS, OPTOUT_LOADING, 'PUT');
		await dispatch(fetchOptedInJackpots());
	};
};

export const fetchMultipleJackpotDetails = jackpots => {
	return async (dispatch, getState) => {
		if (!jackpots.length) {
			// eslint-disable-next-line no-console
			console.error('No jackpot id provided');
			showGeneralError(dispatch);
			return;
		}
		const state = getState();
		const jackpotDetails = jackpotDetailsSelector(state);
		const inhouseJackpotsMap = new Map();

		// eslint-disable-next-line no-restricted-syntax
		for await (const jackpot of jackpots) {
			// skip fetching if we already have details for these jackpots
			if (jackpotDetails?.[jackpot.id]) {
				// eslint-disable-next-line no-continue
				continue;
			}

			const jackpotResponse = await sendRequest(
				dispatch,
				`detail/${jackpot.id}`,
				SET_JACKPOT_DETAILS,
				JACKPOT_DETAILS_LOADING
			);

			inhouseJackpotsMap.set(jackpot.id, jackpotResponse);
		}

		// eslint-disable-next-line no-restricted-syntax
		for (const [_, jackpotDetail] of inhouseJackpotsMap) {
			if (jackpotDetail.premiumJackpotId) {
				const premiumJackpotDetail = inhouseJackpotsMap.get(jackpotDetail.premiumJackpotId);
				inhouseJackpotsMap.set(jackpotDetail.premiumJackpotId, { ...premiumJackpotDetail, isPremium: true });
			}
		}

		const mainJPCount = Array.from(inhouseJackpotsMap.values()).filter(item => !item.isPremium).length;

		// if there are more than 1 main jackpots or no main jackpots at all, show error
		if (mainJPCount > 1 || (!mainJPCount && !Object.keys(jackpotDetails).length)) {
			showGeneralError(dispatch);
			return;
		}

		if (inhouseJackpotsMap.size) {
			const mainJackpot = Array.from(inhouseJackpotsMap.values()).find(item => !item.isPremium);
			const premiumJackpot = Array.from(inhouseJackpotsMap.values()).find(item => item.isPremium);

			dispatch(setJackpotId({ id: mainJackpot?.id }));

			if (premiumJackpot) {
				dispatch(setJackpotId({ isPremium: true, id: premiumJackpot?.id }));
			}

			const jackpotTiers = jackpotTiersSelector(state);

			// if tier data not filled yet from web socket, fill it from the fetch responses above
			if (!jackpotTiers || !jackpotTiers?.length) {
				dispatch({
					type: SET_JACKPOT_TIERS,
					payload: {
						tiers: mainJackpot?.tiers,
						premiumTiers: premiumJackpot?.tiers,
						premiumJackpotId: premiumJackpot?.id,
						premiumJackpotDescription: premiumJackpot?.desc,
						currency: mainJackpot?.currency
					}
				});
			}
		}
	};
};
// thunks END

// reducers START
function reducer(state = initialState, { type, payload }) {
	switch (type) {
		case SET_OPTEDIN_JACKPOTS:
			return { ...state, optedinInhouseJackpots: payload?.jackpots };
		case OPTIN_LOADING:
			return { ...state, optinLoading: payload };
		case OPTOUT_LOADING:
			return { ...state, optoutLoading: payload };
		case FETCH_OPTEDIN_JACKPOTS_LOADING:
			return { ...state, fetchOptedinJackpotsLoading: payload };
		case SET_IS_MODAL_SHOWN:
			return { ...state, isModalShown: payload };
		case JACKPOT_DETAILS_LOADING:
			return { ...state, jackpotDetailsLoading: payload };
		case SET_JACKPOT_DETAILS: {
			const { id, ...rest } = payload || {};
			if (!id) {
				return state;
			}

			return { ...state, jackpotDetails: { ...state.jackpotDetails, [id]: { id, ...rest } } };
		}
		case SET_MAIN_INHOUSE_JP_ID:
		case SET_PREMIUM_INHOUSE_JP_ID: {
			return {
				...state,
				...{ [payload.isPremium ? 'premiumInhouseJackpotId' : 'mainInhouseJackpotId']: payload.id }
			};
		}
		case SET_JACKPOT_TIERS: {
			// ggr is coming from service and we don't need to show it thus filtering it out
			const ggrFilterFunc = item => item.id !== 'ggr';
			const tiers = payload.tiers?.filter(ggrFilterFunc) || [];

			const premiumTiers =
				payload.premiumTiers?.filter(ggrFilterFunc).map(item => ({
					...item,
					isPremium: true,
					premiumJackpotId: payload.premiumJackpotId,
					description: payload.premiumJackpotDescription
				})) || [];
			const combinedTiers = premiumTiers.concat(tiers);

			return { ...state, jackpotTiers: addFormattedTierAmounts(combinedTiers, payload.currency) };
		}
		case UPDATE_JACKPOT_TIERS: {
			const newTiers = {};
			const { jackpots, currency } = payload;

			// create an object with tier name and amount
			jackpots.forEach(tier => {
				newTiers[tier.name] = tier.amount;
			});

			// to prevent unnecessary console error logs if there is an issue with web socket messages
			if (!state.jackpotTiers) {
				return state;
			}

			// create a new updated tiers array
			const updatedTiers = state.jackpotTiers.map(tier => {
				return {
					...tier,
					value: newTiers[tier.id]
				};
			});

			return {
				...state,
				jackpotTiers: addFormattedTierAmounts(updatedTiers, currency)
			};
		}
		case SET_IS_WIN_ANIMATION_SHOWN:
			return { ...state, isWinAnimationShown: payload };
		case SET_WIN_ANIMATION_DATA:
			return { ...state, winAnimationData: payload };
		case INVALIDATE_INHOUSE_JACKPOT_STATE:
			return initialState;
		case JACKPOT_GAME_CLOSED:
			return { ...state, isInGame: false };
		case JACKPOT_GAME_STARTED:
			return { ...state, isInGame: true };
		default:
			return state;
	}
}
// reducers END

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
